import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import { SaleLot, useLotGridEntity } from "@sales/data/lots/LotData";
import { SaleLotsDataProvider, useLotDataGetSaleLotDataQuery } from "@sales/data/lots/LotDataQueries";
import LotStatusBadge from "../../components/badges/LotStatusBadge";
import { LotActionButton, LotLink } from "../../components/buttons/SaleLotButtons";
import LotDialog from "../../dialogs/LotDialog";
import _ from "lodash";
import React from "react";
import { gridItems } from "@agtech/web/components/Grids/Data/AgTechGridProps";
import SaleLotImportModal from "../../modals/SaleLotImportModal";
import { useBidderDataGetSaleBidderDataQuery } from "@sales/data/bidders/BidderDataQueries";
import { useAgTechQueryCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import AgTechGridLink from "@agtech/web/components/Grids/Controls/AgTechGridLink";
import SaleContactDetailDialog from "../../../contacts/dialogs/SaleContactDetailDialog";

export type SaleLotViewGridProps = {
    lotFilter?: (lot: SaleLot) => boolean
}

const SaleLotViewGrid = (props: SaleLotViewGridProps) => {
    const gridQuery = useLotDataGetSaleLotDataQuery();

    return (
        <AgTechQueryLoadingSurface query={gridQuery}>
            {(lotData) => {
                const bidders = Object.values(lotData.bidders);
                const lotGridEntities = useLotGridEntity(lotData, props.lotFilter);
            
                return (
                    <div className="flexed stretched flex">
                        <AgTechGrid
                            name={'SaleLotViewGrid'}
                            header={{
                                title: 'Sale Lots',
                                content: () => (
                                    <div className='column-based horizontal-pane container'>
                                        <LotDialog.Button />
                                    </div>
                                )
                            }}
                            data={{
                                itemQuery: gridItems(_.orderBy(lotGridEntities, lt => lt.saleOrder))
                            }}
                            columns={[
                                {
                                    header: 'Sale Order',
                                    value: l => l.saleOrder,
                                    classes: 'bg-slate-100 emphasized centered',
                                    width: '100px'
                                },
                                {
                                    header: 'Lot Number',
                                    value: l => (
                                        <LotLink lot={l} lotCategories={lotData.lotCategories} classes='mx-3 !text-sm' />
                                    ),
                                    width: '120px'
                                },
                                {
                                    header: 'Purchasing Bidder',
                                    value: l => {
                                        if (l.bidderNumber) {
                                            const lotBidder = bidders.find(b => b.bidderNumber === l.bidderNumber);
                                            const bidderContact = lotBidder?.contact;

                                            if (bidderContact) {
                                                return (
                                                    <AgTechGridLink
                                                        content={`${l.bidderNumber} - ${bidderContact.firstName} ${bidderContact.lastName}`}
                                                        onClick={e => SaleContactDetailDialog.Open(e, {
                                                            contactId: bidderContact.id
                                                        })}
                                                    />
                                                )
                                            }
                                            else {
                                                return l.bidderNumber;
                                            }
                                        }

                                        return '';
                                    },
                                },
                                {
                                    header: 'Purchase Price ($)',
                                    value: l => l.purchasePrice && l.purchasePrice > 0 ? AgTechMoneyFormatter.format(l.purchasePrice) : '',
                                    width: '150px'
                                },
                                {
                                    header: '',
                                    value: l => <LotStatusBadge lot={l} classes="border-0 stretched font-semibold !text-base" />,
                                    width: '100px'
                                }
                            ]}
                            isRowEntityTheSame={(prev, curr) => {
                                return prev.saleOrder === curr.saleOrder &&
                                    prev.lotNumber === curr.lotNumber &&
                                    prev.bidderNumber === curr.bidderNumber &&
                                    prev.purchasePrice === curr.purchasePrice &&
                                    prev.lotCategoryId === curr.lotCategoryId &&
                                    prev.isInvoiced === curr.isInvoiced &&
                                    prev.lotCategoryColor === curr.lotCategoryColor;
                            }}
                            actions={{
                                rendering: l => <LotActionButton lot={l} />
                            }}
                            searchConfig={{
                                options: [
                                    { field: 'Lot Number', value: l => l.lotNumber },
                                    { field: 'Purchasing Bidder', value: l => l.bidderNumber ?? '' },
                                    { field: 'Category', value: l => l.lotCategoryId ? lotData.lotCategories[l.lotCategoryId].name : ''}
                                ]
                            }}
                            // viewConfig={{
                            //     views: [
                            //         { name: 'All Lots', description: 'View all lots in this sale', filter: lot => true },
                            //         { name: 'Lots not sold', description: 'View only lots not yet sold in this sale', filter: lot => !isLotSold(lot) },
                            //         { name: 'Invoiced lots', description: 'View only invoiced lots in this sale', filter: lot => lot.isInvoiced },
                            //     ]
                            // }}
                            noRecordsConfig={{
                                message: "We're sorry, there are no lots in the system for this sale.",
                                action: {
                                    prompt: 'To create one, please click the button below',
                                    button: () => (
                                        <div className='flex-row gap-x-3 flex'>
                                            <LotDialog.Button classes='button-highlight' />
                                            <SaleLotImportModal.Button classes='button-highlight' />
                                        </div>
                                    )
                                }
                            }}
                            sortingConfig={{
                                getSortingKey: lot => lot.saleOrder
                            }}
                        />
                    </div>
                )
            }}
        </AgTechQueryLoadingSurface>
    );
};

export default React.memo(SaleLotViewGrid, () => true) as typeof SaleLotViewGrid;